import {
  CssBaseline,
  ThemeProvider as MuiAppThemeProvider,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { getAssetPath } from '@/constants/configs';

import { useAppSelector } from '@/hooks/redux';

import { selectConfigsState } from '@/redux/configs';

import { injectCaymanFonts } from '@/styles/cayman/fonts';
import { caymanTheme as CaymanTheme } from '@/styles/cayman/theme';

import { Tenant, TenantThemeConfigName } from '@/types/common';

import { isCaymanDomain } from '@/utils/domain';

import { defaultTheme } from '@/styles';

const CaymanThemeConfig = {
  config: CaymanTheme,
  logo: getAssetPath('/cayman-logo-with-text.svg'),
  name: 'cayman',
};

const DefaultThemeConfig = {
  config: defaultTheme,
  logo: getAssetPath('/logo-with-text.svg'),
  name: 'default',
};

const initTheme: ThemeProps = isCaymanDomain
  ? CaymanThemeConfig
  : DefaultThemeConfig;

if (isCaymanDomain) {
  injectCaymanFonts();
}

type ThemeProps = {
  config: Theme;
  logo: string;
  name: string;
};

type ThemeContextProps = {
  theme: ThemeProps;
  setTheme: (tenant: Tenant) => Promise<void>;
};

const ThemeContext = createContext<ThemeContextProps>({
  theme: initTheme,
  setTheme: () => Promise.resolve(),
});

type AppThemeProviderProps = {
  children: ReactNode;
};

export const AppThemeProvider: React.FC<AppThemeProviderProps> = ({
  children,
}) => {
  const [theme, _setTheme] = useState<ThemeProps>(initTheme);
  const { tenant } = useAppSelector(selectConfigsState);

  const updateThemeConfig = useCallback(
    async (tenantValue: Tenant) => {
      let themeConfig;

      const tenantConfigName = TenantThemeConfigName[tenantValue.toLowerCase()];
      // TODO: Find out why MUI dropdown does not close if using single-ton theme mechanism
      // dynamic import is a workaround to avoid the issue
      try {
        if (tenantConfigName && tenantConfigName !== theme.name) {
          if (tenantValue === Tenant.Cayman) {
            injectCaymanFonts();
            _setTheme(CaymanThemeConfig);
          } else {
            themeConfig = await import(
              `../styles/${tenantConfigName}/theme.ts`
            );
            _setTheme({
              config: themeConfig.defaultTheme,
              logo: getAssetPath(`/${tenantConfigName}-logo-with-text.svg`),
              name: tenantConfigName,
            });
          }
        }
      } catch (error) {
        console.error('Error updating theme config', error);
      }
    },
    [_setTheme, theme.name],
  );

  useEffect(() => {
    if (tenant) {
      updateThemeConfig(tenant);
    }
  }, [tenant, updateThemeConfig]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme: updateThemeConfig }}>
      <MuiAppThemeProvider theme={theme.config}>
        <CssBaseline />
        {children}
      </MuiAppThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useAppTheme = () => {
  const context = useContext(ThemeContext);
  return context;
};

export default ThemeContext;
